/**
 * Admin → Marketing → Ads — DTOs for `/api/admin/ads/*`
 * (apps/api/src/routes/admin-ads.ts, spec `_docs/marketing-automation-spec.md` §3 WP-B).
 *
 * The constants mirror `packages/marketing/src/ads/types.ts`. They are copied,
 * not imported: `@pontimed/marketing` pulls in `@pontimed/db`, and the admin
 * SPA must never import the Prisma client (Architecture Discipline Rule 2).
 *
 * Every `normalize*` below reads the response defensively — a missing array
 * becomes `[]`, a missing number `null` — so one odd row (or an older API
 * build during a deploy) degrades a card to "—" instead of blanking the page.
 * The shapes each function expects are the contract; see the JSDoc on each.
 *
 * Hard boundaries the UI restates rather than enforces (the API enforces them):
 *   - Conversions are B2B only: a claimed profile or a first paid subscription.
 *   - The only write the platform adapters ever make is PAUSE.
 */

export const AD_PLATFORMS = ["meta", "google"] as const;
export type AdPlatform = (typeof AD_PLATFORMS)[number];

export const AD_CONVERSION_NAMES = ["profile_claimed", "paid_subscription"] as const;
export type AdConversionName = (typeof AD_CONVERSION_NAMES)[number];

export const AD_CONVERSION_STATUSES = ["pending", "sent", "failed", "skipped"] as const;
export type AdConversionStatus = (typeof AD_CONVERSION_STATUSES)[number];

export const PLATFORM_LABEL: Record<AdPlatform, string> = {
  meta: "Meta",
  google: "Google Ads",
};

// ---------------------------------------------------------------------------
// Connections — GET /connections, POST /connections, PATCH /connections/:id
// ---------------------------------------------------------------------------

/**
 * `AdAccountConnection.config` (Prisma JSON):
 *   meta:   { pixelId }                                   — Conversions API dataset
 *   google: { loginCustomerId, conversionActions: { profile_claimed, paid_subscription } }
 *           (conversion action resource names, `customers/<id>/conversionActions/<id>`)
 */
export interface AdConnectionConfig {
  pixelId?: string | null;
  loginCustomerId?: string | null;
  conversionActions?: Partial<Record<AdConversionName, string | null>> | null;
}

export interface AdConnection {
  id: string;
  platform: AdPlatform;
  /** Meta: `act_<digits>`. Google: 10-digit customer id, digits only. */
  externalAccountId: string;
  name: string | null;
  currency: string;
  countryCode: string | null;
  /** `pending` until credentials work; `active`, `error`, or a platform-specific value. */
  status: string;
  /** An encrypted token is stored. The token itself is never returned by the API. */
  hasCredentials: boolean;
  tokenExpiresAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  config: AdConnectionConfig;
  dailyCapMinor: number | null;
  /** Monthly spend cap (current calendar month), minor units. */
  spendCapMinor: number | null;
  alertEmail: string | null;
  autoPauseEnabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  campaignCount: number | null;
}

/** POST /connections body (create). `accessToken` is Meta-only and write-only. */
export interface AdConnectionCreateInput {
  platform: AdPlatform;
  externalAccountId: string;
  name: string | null;
  currency: string;
  countryCode: string | null;
  config: AdConnectionConfig;
  accessToken?: string;
}

/** PATCH /connections/:id body. Omitting `accessToken` keeps the stored token. */
export type AdConnectionUpdateInput = Omit<
  AdConnectionCreateInput,
  "platform" | "externalAccountId"
>;

/** PATCH /connections/:id/guardrails body — amounts in minor units. */
export interface AdGuardrailsInput {
  dailyCapMinor: number | null;
  spendCapMinor: number | null;
  alertEmail: string | null;
  autoPauseEnabled: boolean;
}

// ---------------------------------------------------------------------------
// Campaigns — GET /campaigns?connectionId=
// ---------------------------------------------------------------------------

export interface AdCampaign {
  id: string;
  connectionId: string | null;
  platform: AdPlatform | null;
  externalId: string;
  name: string;
  /** Normalised platform status: "active" | "paused" | "removed" | raw value. */
  status: string | null;
  objective: string | null;
  dailyBudgetMinor: number | null;
  currency: string | null;
  /** Spend over the last 7 days, minor units, in `currency`. */
  spend7dMinor: number | null;
  autoPausedAt: string | null;
  autoPauseReason: string | null;
  connectionName: string | null;
}

// ---------------------------------------------------------------------------
// Conversions — GET /conversions?status=&limit=&offset=
// ---------------------------------------------------------------------------

export interface AdConversion {
  id: string;
  connectionId: string | null;
  platform: AdPlatform | null;
  eventName: string;
  eventTime: string | null;
  valueMinor: number | null;
  currency: string | null;
  status: string;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  createdAt: string | null;
  connectionName: string | null;
}

export interface AdConversionPage {
  items: AdConversion[];
  total: number;
  /** Per-status totals across the whole outbox (for the filter chips), when the API sends them. */
  counts: Partial<Record<string, number>>;
}

// ---------------------------------------------------------------------------
// Overview — GET /overview?from=YYYY-MM-DD&to=YYYY-MM-DD
// ---------------------------------------------------------------------------

export interface AdsPlatformSummary {
  platform: AdPlatform;
  currency: string | null;
  spendMinor: number | null;
  impressions: number | null;
  clicks: number | null;
  /** What the ad platform itself reports as conversions (its own pixel/tag). */
  platformConversions: number | null;
  /** Profiles claimed by leads attributed to this platform (our DB). */
  claimed: number | null;
  /** First paid subscriptions attributed to this platform (our DB). */
  paid: number | null;
  costPerClaimedMinor: number | null;
  costPerPaidMinor: number | null;
}

export interface AdsDailyRow {
  date: string;
  platform: AdPlatform | null;
  spendMinor: number;
  currency: string | null;
}

export interface AdsOverview {
  from: string | null;
  to: string | null;
  platforms: AdsPlatformSummary[];
  daily: AdsDailyRow[];
  notes: string[];
}

// ---------------------------------------------------------------------------
// Action responses
// ---------------------------------------------------------------------------

/** POST /connections/:id/test — `AdsHealth`, optionally wrapped as `{ health }`. */
export interface AdTestResult {
  ok: boolean;
  error: string | null;
}

/** POST /connections/:id/sync — inline result or a queued job. */
export interface AdSyncResult {
  ok: boolean;
  queued: boolean;
  campaigns: number | null;
  metrics: number | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Defensive readers
// ---------------------------------------------------------------------------

type Obj = Record<string, unknown>;

function asObj(value: unknown): Obj | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Obj)
    : null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function num(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function firstNum(...values: unknown[]): number | null {
  for (const v of values) {
    const n = num(v);
    if (n !== null) return n;
  }
  return null;
}

function platformOf(value: unknown): AdPlatform | null {
  return value === "meta" || value === "google" ? value : null;
}

/** `data` itself when it is an array, else the first array found under one of `keys`. */
export function pickArray(data: unknown, keys: string[]): unknown[] {
  if (Array.isArray(data)) return data;
  const o = asObj(data);
  if (!o) return [];
  for (const key of keys) {
    if (Array.isArray(o[key])) return o[key] as unknown[];
  }
  return [];
}

function countsRecord(value: unknown): Partial<Record<string, number>> {
  const o = asObj(value);
  if (!o) return {};
  const out: Partial<Record<string, number>> = {};
  for (const [k, v] of Object.entries(o)) {
    const n = num(v);
    if (n !== null) out[k] = n;
  }
  return out;
}

function normalizeConfig(value: unknown): AdConnectionConfig {
  const o = asObj(value);
  if (!o) return {};
  const actions = asObj(o.conversionActions);
  return {
    pixelId: str(o.pixelId),
    loginCustomerId: str(o.loginCustomerId),
    conversionActions: actions
      ? {
          profile_claimed: str(actions.profile_claimed),
          paid_subscription: str(actions.paid_subscription),
        }
      : null,
  };
}

/** One connection row. Rows without an id or a known platform are dropped. */
export function normalizeConnection(raw: unknown): AdConnection | null {
  const o = asObj(raw);
  if (!o) return null;
  const id = str(o.id);
  const platform = platformOf(o.platform);
  if (!id || !platform) return null;
  const count = asObj(o._count);
  return {
    id,
    platform,
    externalAccountId: str(o.externalAccountId) ?? "",
    name: str(o.name),
    currency: str(o.currency) ?? "EUR",
    countryCode: str(o.countryCode),
    status: str(o.status) ?? "pending",
    hasCredentials: o.hasCredentials === true,
    tokenExpiresAt: str(o.tokenExpiresAt),
    lastSyncAt: str(o.lastSyncAt),
    lastError: str(o.lastError),
    config: normalizeConfig(o.config),
    dailyCapMinor: num(o.dailyCapMinor),
    spendCapMinor: num(o.spendCapMinor),
    alertEmail: str(o.alertEmail),
    autoPauseEnabled: o.autoPauseEnabled === true,
    createdAt: str(o.createdAt),
    updatedAt: str(o.updatedAt),
    campaignCount: firstNum(o.campaignCount, count?.campaigns),
  };
}

/** GET /connections → `{ connections: [...] }` (or a bare array / `{ items }`). */
export function normalizeConnections(data: unknown): AdConnection[] {
  return pickArray(data, ["connections", "items"])
    .map(normalizeConnection)
    .filter((c): c is AdConnection => c !== null);
}

/** POST/PATCH /connections → the connection, bare or as `{ connection }`. */
export function normalizeConnectionResponse(data: unknown): AdConnection | null {
  const o = asObj(data);
  return normalizeConnection(o && asObj(o.connection) ? o.connection : data);
}

/** GET /campaigns → `{ campaigns: [...] }` (or a bare array / `{ items }`). */
export function normalizeCampaigns(data: unknown): AdCampaign[] {
  return pickArray(data, ["campaigns", "items"])
    .map((raw): AdCampaign | null => {
      const o = asObj(raw);
      if (!o) return null;
      const id = str(o.id);
      if (!id) return null;
      const connection = asObj(o.connection);
      return {
        id,
        connectionId: str(o.connectionId) ?? str(connection?.id),
        platform: platformOf(o.platform) ?? platformOf(connection?.platform),
        externalId: str(o.externalId) ?? "",
        name: str(o.name) ?? str(o.externalId) ?? id,
        status: str(o.status),
        objective: str(o.objective),
        dailyBudgetMinor: num(o.dailyBudgetMinor),
        currency: str(o.currency) ?? str(connection?.currency),
        spend7dMinor: firstNum(o.spend7dMinor, o.spendLast7dMinor, o.last7dSpendMinor),
        autoPausedAt: str(o.autoPausedAt),
        autoPauseReason: str(o.autoPauseReason),
        connectionName:
          str(o.connectionName) ?? str(connection?.name) ?? str(connection?.externalAccountId),
      };
    })
    .filter((c): c is AdCampaign => c !== null);
}

/** GET /conversions → `{ conversions: [...], total, counts }`. */
export function normalizeConversionPage(data: unknown): AdConversionPage {
  const o = asObj(data);
  const items = pickArray(data, ["conversions", "items", "events"])
    .map((raw): AdConversion | null => {
      const r = asObj(raw);
      if (!r) return null;
      const id = str(r.id);
      if (!id) return null;
      const connection = asObj(r.connection);
      return {
        id,
        connectionId: str(r.connectionId) ?? str(connection?.id),
        platform: platformOf(r.platform) ?? platformOf(connection?.platform),
        eventName: str(r.eventName) ?? "",
        eventTime: str(r.eventTime),
        valueMinor: num(r.valueMinor),
        currency: str(r.currency),
        status: str(r.status) ?? "pending",
        attempts: num(r.attempts) ?? 0,
        lastError: str(r.lastError),
        sentAt: str(r.sentAt),
        createdAt: str(r.createdAt),
        connectionName:
          str(r.connectionName) ?? str(connection?.name) ?? str(connection?.externalAccountId),
      };
    })
    .filter((c): c is AdConversion => c !== null);
  return {
    items,
    total: num(o?.total) ?? items.length,
    counts: countsRecord(o?.counts ?? o?.byStatus),
  };
}

function normalizePlatformSummary(raw: unknown, platformKey?: string): AdsPlatformSummary | null {
  const o = asObj(raw);
  if (!o) return null;
  const platform = platformOf(o.platform) ?? platformOf(platformKey);
  if (!platform) return null;
  return {
    platform,
    currency: str(o.currency),
    spendMinor: num(o.spendMinor),
    impressions: num(o.impressions),
    clicks: num(o.clicks),
    platformConversions: firstNum(o.platformConversions, o.conversions),
    claimed: firstNum(o.claimed, o.claimedProfiles, o.profileClaimed),
    paid: firstNum(o.paid, o.paidSubscriptions, o.paidSubscription),
    costPerClaimedMinor: firstNum(o.costPerClaimedMinor, o.costPerClaimedProfileMinor),
    costPerPaidMinor: firstNum(o.costPerPaidMinor, o.costPerPaidSubscriptionMinor),
  };
}

/**
 * GET /overview →
 *   { from, to,
 *     byPlatform: [{ platform, currency, spendMinor, impressions, clicks, platformConversions,
 *                    claimed, paid, costPerClaimedMinor, costPerPaidMinor }],
 *     daily: [{ date: "YYYY-MM-DD", platform, spendMinor }],
 *     notes: string[] }
 * (`platforms`, or a record keyed by platform, is read too.) `currency` is null
 * when one platform's accounts bill in different currencies — `notes` says so.
 */
export function normalizeOverview(data: unknown): AdsOverview {
  const o = asObj(data) ?? {};
  const rawPlatforms = o.byPlatform ?? o.platforms;
  let platforms: AdsPlatformSummary[] = [];
  if (Array.isArray(rawPlatforms)) {
    platforms = rawPlatforms
      .map((p) => normalizePlatformSummary(p))
      .filter((p): p is AdsPlatformSummary => p !== null);
  } else if (asObj(rawPlatforms)) {
    platforms = Object.entries(asObj(rawPlatforms) ?? {})
      .map(([key, p]) => normalizePlatformSummary(p, key))
      .filter((p): p is AdsPlatformSummary => p !== null);
  }
  platforms.sort((a, b) => AD_PLATFORMS.indexOf(a.platform) - AD_PLATFORMS.indexOf(b.platform));

  const daily = pickArray(o, ["daily", "days", "series"])
    .map((raw): AdsDailyRow | null => {
      const r = asObj(raw);
      const date = str(r?.date);
      if (!r || !date) return null;
      return {
        date: date.slice(0, 10),
        platform: platformOf(r.platform),
        spendMinor: num(r.spendMinor) ?? 0,
        currency: str(r.currency),
      };
    })
    .filter((r): r is AdsDailyRow => r !== null);

  const notes = pickArray(o, ["notes"])
    .map((n) => (typeof n === "string" ? n : (str(asObj(n)?.message) ?? str(asObj(n)?.text))))
    .filter((n): n is string => !!n);

  return { from: str(o.from), to: str(o.to), platforms, daily, notes };
}

export function normalizeTestResult(data: unknown): AdTestResult {
  const o = asObj(data);
  const inner = asObj(o?.health) ?? o;
  return {
    ok: inner?.ok === true,
    error: str(inner?.error) ?? str(o?.error),
  };
}

export function normalizeSyncResult(data: unknown): AdSyncResult {
  const o = asObj(data) ?? {};
  const queued = o.queued === true || o.enqueued === true || typeof o.jobId === "string";
  return {
    ok: o.ok !== false,
    queued,
    campaigns: firstNum(o.campaigns, o.campaignCount),
    metrics: firstNum(o.metrics, o.metricRows, o.rows),
    error: str(o.error),
  };
}

/**
 * The server's error message from an ApiError body (`{ error, message }`),
 * or null. Used for inline form errors; never shows a stack or raw HTML.
 */
export function apiErrorText(body: unknown): string | null {
  const o = asObj(body);
  if (!o) return null;
  // `message` is the human sentence; `error` is often a code (invalid_config).
  return str(o.message) ?? str(o.error);
}
